import type { GameState } from '../state';
import { add, sub, mul, gte, gt, D, type Decimal } from '../math/num';
import { getFrame } from '../data/frames';
import { getPartDef, upgradeCost } from '../data/parts/partDefs';
import { addNode, addEdge, removeNode as removeNodeFromGraph, removeEdge, nodeForInstance } from './factory';
import type { MindGraph, PortSpec } from './types';
import { equippedWeight, weightCapacity, partWeight, findInstance } from '../sim/weight';
import { RARITY_INDEX } from '../data/parts/partTypes';
import { isMechCoreInstance, portsForNode } from '../data/mechCore';

/**
 * Validated Mind editing (THE_MIND_AND_PARTS.md). All mutations go through these
 * pure functions so the UI can't build an illegal Mind: slot caps, Weight,
 * CPU/RAM seam, typed-port + flow/modifier compatibility.
 */

export interface OpResult {
  ok: boolean;
  reason?: string;
}

function port(defId: string, portId: string, tier: number): PortSpec | undefined {
  return portsForNode(defId, tier).find((p) => p.id === portId);
}

export function frameTier(state: GameState): number {
  return getFrame(state.frame.defId).tier;
}

export function nodeSlotLimit(state: GameState): number {
  return getFrame(state.frame.defId).nodeSlots;
}

function countCategory(state: GameState, category: string): number {
  return state.mind.nodes.filter((n) => getPartDef(n.defId).category === category).length;
}

export function isEquipped(state: GameState, instanceId: string): boolean {
  return state.mind.nodes.some((n) => n.instanceId === instanceId);
}

export function canEquipPart(state: GameState, instanceId: string): OpResult {
  const inst = findInstance(state, instanceId);
  if (!inst) return { ok: false, reason: 'No such part.' };
  if (isEquipped(state, instanceId)) return { ok: false, reason: 'Already equipped.' };
  if (state.mind.nodes.length >= nodeSlotLimit(state)) {
    return { ok: false, reason: 'No free node slots. A bigger Mech grows the board.' };
  }
  const def = getPartDef(inst.defId);
  if (def.category === 'attack' && countCategory(state, 'attack') >= state.resources.cpu) {
    return { ok: false, reason: `CPU limit reached (max ${state.resources.cpu} attacks).` };
  }
  const w = add(equippedWeight(state), partWeight(inst.defId, inst));
  if (gt(w, weightCapacity(state))) return { ok: false, reason: 'Over Weight budget.' };
  return { ok: true };
}

export function equipPart(state: GameState, instanceId: string, x = 120, y = 120): OpResult {
  const check = canEquipPart(state, instanceId);
  if (!check.ok) return check;
  const inst = findInstance(state, instanceId)!;
  addNode(state.mind, inst.defId, instanceId, x, y, inst.level);
  return { ok: true };
}

export function unequipPart(state: GameState, instanceId: string): OpResult {
  if (isMechCoreInstance(instanceId)) return { ok: false, reason: 'The Mech Core is part of you. It cannot be removed.' };
  const node = nodeForInstance(state.mind, instanceId);
  if (!node) return { ok: false, reason: 'Not equipped.' };
  removeNodeFromGraph(state.mind, node.id);
  return { ok: true };
}

export function deleteNode(state: GameState, nodeId: string): OpResult {
  const node = state.mind.nodes.find((n) => n.id === nodeId);
  if (!node) return { ok: false, reason: 'No such node.' };
  if (isMechCoreInstance(node.instanceId)) return { ok: false, reason: 'The Mech Core is part of you. It cannot be removed.' };
  removeNodeFromGraph(state.mind, nodeId);
  return { ok: true };
}

export function moveNode(state: GameState, nodeId: string, x: number, y: number): OpResult {
  const node = state.mind.nodes.find((n) => n.id === nodeId);
  if (!node) return { ok: false, reason: 'No such node.' };
  node.x = x;
  node.y = y;
  return { ok: true };
}

/** Whether a part is locked from scrap / melt / forge. */
export function isProtected(state: GameState, instanceId: string): boolean {
  return state.inventory.protectedIds.includes(instanceId);
}

/** Toggle a part's protected lock (equipped parts may be protected too). */
export function toggleProtect(state: GameState, instanceId: string): OpResult {
  const ids = state.inventory.protectedIds;
  const i = ids.indexOf(instanceId);
  if (i >= 0) ids.splice(i, 1); else ids.push(instanceId);
  return { ok: true };
}

/** Scrap a part for Scrap (the inventory sink). Cannot scrap an equipped or
 *  protected part. */
export function scrapPart(state: GameState, instanceId: string): OpResult {
  if (isProtected(state, instanceId)) return { ok: false, reason: 'Part is protected. Unlock it to scrap.' };
  if (isEquipped(state, instanceId)) return { ok: false, reason: 'Unequip before scrapping.' };
  const idx = state.inventory.parts.findIndex((p) => p.instanceId === instanceId);
  if (idx < 0) return { ok: false, reason: 'No such part.' };
  const inst = state.inventory.parts[idx]!;
  const value = scrapValue(inst.rarity, inst.level);
  state.wallet.scrap = add(state.wallet.scrap, value);
  state.inventory.parts.splice(idx, 1);
  return { ok: true };
}

export function scrapValue(rarity: keyof typeof RARITY_INDEX, level: number): Decimal {
  return mul(D(8 + RARITY_INDEX[rarity] * 18), D(level));
}

/** Upgrade an equipped or stored part with Components. */
export function upgradePart(state: GameState, instanceId: string): OpResult {
  const inst = findInstance(state, instanceId);
  if (!inst) return { ok: false, reason: 'No such part.' };
  const cost = upgradeCost(getPartDef(inst.defId), inst.level);
  if (!gte(state.wallet.components, cost)) return { ok: false, reason: 'Not enough Components.' };
  state.wallet.components = sub(state.wallet.components, cost);
  inst.level += 1;
  const node = nodeForInstance(state.mind, instanceId);
  if (node) node.level = inst.level; // keep node mirror in sync
  return { ok: true };
}

export function partUpgradeCost(state: GameState, instanceId: string): Decimal | null {
  const inst = findInstance(state, instanceId);
  if (!inst) return null;
  return upgradeCost(getPartDef(inst.defId), inst.level);
}

// ---- wiring ----

function roleOf(spec: PortSpec): 'flow' | 'modifier' {
  return spec.role ?? 'flow';
}

export function canConnect(graph: MindGraph, fromNode: string, fromPort: string, toNode: string, toPort: string, tier = 1): OpResult {
  if (fromNode === toNode) return { ok: false, reason: 'A node cannot wire to itself.' };
  const from = graph.nodes.find((n) => n.id === fromNode);
  const to = graph.nodes.find((n) => n.id === toNode);
  if (!from || !to) return { ok: false, reason: 'Missing node.' };

  const outPort = port(from.defId, fromPort, tier);
  const inPort = port(to.defId, toPort, tier);
  if (!outPort || outPort.direction !== 'out') return { ok: false, reason: 'Not an output port.' };
  if (!inPort || inPort.direction !== 'in') return { ok: false, reason: 'Not an input port.' };
  if (roleOf(outPort) !== roleOf(inPort)) {
    return { ok: false, reason: roleOf(outPort) === 'modifier' ? 'Tuners only plug into modifier ports.' : 'Power only flows into flow ports.' };
  }
  // Modifier ports take exactly one Tuner. Flow inputs allow FAN-IN (taps sum =
  // the additive supercharge), so they're only blocked against a duplicate wire.
  if (roleOf(inPort) === 'modifier' && graph.edges.some((e) => e.toNode === toNode && e.toPort === toPort)) {
    return { ok: false, reason: 'That modifier port already has a Tuner.' };
  }
  if (graph.edges.some((e) => e.fromNode === fromNode && e.fromPort === fromPort && e.toNode === toNode && e.toPort === toPort)) {
    return { ok: false, reason: 'Already wired.' };
  }
  return { ok: true };
}

export function connect(state: GameState, fromNode: string, fromPort: string, toNode: string, toPort: string): OpResult {
  // Wiring is never limited (P57): the booster stacking penalty in the
  // evaluator is the balance lever, not a wire count.
  const check = canConnect(state.mind, fromNode, fromPort, toNode, toPort, frameTier(state));
  if (!check.ok) return check;
  addEdge(state.mind, fromNode, fromPort, toNode, toPort);
  return { ok: true };
}

export function disconnect(state: GameState, edgeId: string): OpResult {
  if (!state.mind.edges.some((e) => e.id === edgeId)) return { ok: false, reason: 'No such wire.' };
  removeEdge(state.mind, edgeId);
  return { ok: true };
}
